import { Seo } from "@/components/layout/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { SectionIntro } from "@/components/sections/SectionIntro";
import { CtaBand } from "@/components/sections/CtaBand";
import { Reveal } from "@/components/motion/Reveal";
import { LogoMark } from "@/components/brand/Logo";
import { VALUES } from "@/content/company";

const MissionVision = () => (
  <section className="bg-ivory py-24 text-ink sm:py-32" data-testid="about-mission-vision">
    <div className="shell grid gap-6 md:grid-cols-2">
      {[
        { k: "Mission", t: "To empower individuals, businesses and institutions through practical technology education, intelligent solutions and access to global opportunities.", dark: false },
        { k: "Vision", t: "To build a trusted African technology ecosystem that connects people, innovation, education and opportunity.", dark: true },
      ].map((m, i) => (
        <Reveal key={m.k} delay={i * 0.1} className={`flex min-h-[340px] flex-col justify-between rounded-[28px] p-8 sm:p-12 ${m.dark ? "grain bg-plum text-white" : "border border-ink/15 bg-white"}`} data-testid={`about-${m.k.toLowerCase()}`}>
          <p className={`eyebrow relative z-[2] ${m.dark ? "text-gold-light" : "text-plum-2"}`}>Our {m.k}</p>
          <p className="relative z-[2] mt-10 font-display text-2xl font-light leading-snug sm:text-[2.1rem] sm:leading-[1.25]">{m.t}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Values = () => (
  <section className="bg-ink py-24 sm:py-32" data-testid="about-values">
    <div className="shell">
      <SectionIntro eyebrow="Our Values" title={<>What we <span className="italic text-gold-metal">stand for.</span></>} />
      <ul className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-[28px] border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
        {VALUES.map(({ title, text, icon: Icon }, i) => (
          <Reveal as="li" key={title} delay={i * 0.06} className="group flex min-h-[260px] flex-col bg-ink p-7 transition-colors duration-500 hover:bg-plum-deep" data-testid={`value-${title.toLowerCase()}`}>
            <Icon className="h-6 w-6 text-gold-light transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.4} aria-hidden="true" />
            <h3 className="mt-auto pt-10 font-display text-3xl font-light text-white">{title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-white/70">{text}</p>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const FounderPortrait = () => (
  <div className="relative mx-auto w-full max-w-[440px]" data-testid="founder-photo">
    <div aria-hidden="true" className="absolute -inset-4 rounded-t-[999px] rounded-b-[36px] border border-gold/40" />
    <div className="relative h-[500px] overflow-hidden rounded-t-[999px] rounded-b-[28px] bg-plum sm:h-[580px]">
      <img src="/images/founder.webp" alt="Portrait of Vivian O. Nnodum, Founder & CEO of VION Technology Limited" loading="lazy" className="h-full w-full object-cover object-[50%_20%]" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-plum-deep/70 via-transparent to-transparent" />
      <div className="absolute inset-x-5 bottom-5 flex items-center gap-3 rounded-2xl border border-white/15 bg-ink/70 p-4 backdrop-blur-md">
        <LogoMark className="h-9 w-9 shrink-0" />
        <div className="min-w-0">
          <p className="truncate font-display text-lg text-white">Vivian O. Nnodum</p>
          <p className="eyebrow truncate text-[10px] text-gold-light">Founder &amp; CEO</p>
        </div>
      </div>
    </div>
  </div>
);

const Founder = () => (
  <section className="bg-ivory py-24 text-ink sm:py-32" data-testid="about-founder">
    <div className="shell grid items-center gap-16 lg:grid-cols-12">
      <Reveal className="lg:col-span-5"><FounderPortrait /></Reveal>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal as="p" className="eyebrow flex items-center gap-3 text-plum-2"><span className="h-px w-10 bg-plum-2" /> Founder</Reveal>
        <Reveal as="h2" delay={0.05} className="mt-6 font-display text-5xl font-light tracking-[-0.02em] sm:text-6xl" data-testid="founder-name">Vivian O. Nnodum</Reveal>
        <Reveal as="p" delay={0.08} className="mt-3 text-[13px] font-semibold uppercase tracking-[0.16em] text-plum-2">Founder &amp; CEO, VION Technology Limited</Reveal>
        <Reveal delay={0.12} className="mt-10 space-y-5 text-base leading-relaxed text-ink/80 md:text-lg">
          <p>Vivian O. Nnodum is a technology entrepreneur, business consultant and Computer Science graduate with a growing focus on Artificial Intelligence, automation, cloud technologies, digital transformation and global opportunities.</p>
          <p>She founded VION Technology Limited with a vision to build an ecosystem that helps people develop relevant technology skills, enables businesses to adopt practical technology solutions and connects individuals with opportunities beyond borders.</p>
          <p className="border-l-2 border-gold pl-6 font-display text-xl font-light italic leading-snug text-ink sm:text-2xl">Her work combines technology, education, entrepreneurship and global opportunity development.</p>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <Seo title="About VION Tech | Our Mission, Vision & Founder" description="Learn about VION Technology Limited — an emerging technology and innovation company building an ecosystem around people, innovation and opportunity." path="/about" />
      <PageHero
        testid="about-hero"
        eyebrow="About VION Tech"
        lines={["Building a technology", "ecosystem around people,", "innovation and opportunity."]}
        accentLine={2}
        intro="An emerging technology and innovation company focused on helping individuals, businesses and institutions learn, adopt and benefit from technology."
        image="/images/about-city.webp"
        alt="Aerial view of a busy African city representing growth and opportunity"
        shape="slant"
      />
      <section className="bg-ink pb-24 sm:pb-32" data-testid="about-who-we-are">
        <div className="shell grid gap-12 border-t border-white/10 pt-20 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionIntro eyebrow="Who We Are" title="Who We Are" className="[&_h2]:sr-only" /></div>
          <div className="space-y-6 lg:col-span-8">
            <Reveal as="p" className="font-display text-2xl font-light leading-snug text-white sm:text-[2rem] sm:leading-[1.3]">
              VION Technology Limited (VION Tech) is an emerging technology and innovation company focused on helping individuals, businesses and institutions learn, adopt and benefit from technology.
            </Reveal>
            <Reveal as="p" delay={0.06} className="max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
              Our work sits at the intersection of Artificial Intelligence, technology education, automation, digital transformation and global opportunities.
            </Reveal>
            <Reveal as="p" delay={0.1} className="max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
              Through VION AI Academy, VION AI Solutions and VION Consult, we create practical pathways for people and organisations to learn, transform and grow.
            </Reveal>
          </div>
        </div>
      </section>
      <MissionVision />
      <Values />
      <Founder />
      <CtaBand testid="about-cta" eyebrow="Work With Us" title="Let's Build What's Next." text="Whether you want to learn, transform your organisation or explore global opportunities, we'd love to hear from you." primary={{ label: "Get Started", to: "/contact" }} secondary={{ label: "Partner With Us", to: "/partnerships" }} />
    </>
  );
}
